import {
  clampRankBadgeCropRect,
  exportRankBadgeSheetCrop,
  maxRankBadgeCropRect,
} from "@/rank/rank-badge-crop";
import {
  RANK_BADGE_SHEET_HEIGHT,
  RANK_BADGE_SHEET_WIDTH,
} from "@/rank/leaderboard-types";
import type { PixelRect } from "@/home/sheet-crop";

const DISPLAY_MAX_W = 520;

export type RankBadgeSheetCropOptions = {
  onApplied: (dataUrl: string) => void;
  onCancel?: () => void;
  exportBg?: () => { removeWhiteBg: boolean; removeBlackBg: boolean };
};

export type RankBadgeSheetCropHandle = {
  destroy: () => void;
};

export function mountRankBadgeSheetCrop(
  host: HTMLElement,
  sourceDataUrl: string,
  options: RankBadgeSheetCropOptions,
): RankBadgeSheetCropHandle {
  let cropRect: PixelRect = { sx: 0, sy: 0, sw: 1, sh: 1 };
  let displayScale = 1;
  let dragging = false;
  let dragStart: { x: number; y: number; sx: number; sy: number } | null = null;

  host.innerHTML = `
    <div class="gm-rank-badge-crop-panel">
      <p class="hint gm-rank-badge-crop-hint">ลากกรอบบนภาพ · บันทึกเป็น ${RANK_BADGE_SHEET_WIDTH}×${RANK_BADGE_SHEET_HEIGHT}px (อัตราส่วนคงที่)</p>
      <div class="gm-rank-badge-crop-canvas-wrap">
        <canvas class="gm-rank-badge-crop-canvas" aria-label="ลากกรอบครอปป้ายแรงค์"></canvas>
      </div>
      <div class="gm-rank-badge-crop-foot">
        <button type="button" class="btn btn-ghost btn-sm" id="gm-rank-badge-crop-cancel">ยกเลิกครอป</button>
        <button type="button" class="btn btn-primary btn-sm" id="gm-rank-badge-crop-apply">ใช้การครอป</button>
      </div>
    </div>
  `;

  const canvas = host.querySelector<HTMLCanvasElement>(".gm-rank-badge-crop-canvas");
  const img = new Image();
  img.decoding = "async";

  const computeDisplayScale = (): number => {
    const iw = img.naturalWidth;
    if (!iw) return 1;
    const wrap = host.querySelector<HTMLElement>(".gm-rank-badge-crop-canvas-wrap");
    const pad = 8;
    const wrapW = wrap?.clientWidth ? wrap.clientWidth - pad : DISPLAY_MAX_W;
    const cap = Math.min(DISPLAY_MAX_W, Math.max(200, wrapW));
    return Math.min(1, cap / iw);
  };

  const paint = () => {
    if (!canvas || !img.naturalWidth) return;
    const iw = img.naturalWidth;
    const ih = img.naturalHeight;
    displayScale = computeDisplayScale();
    const dw = Math.round(iw * displayScale);
    const dh = Math.round(ih * displayScale);
    canvas.width = dw;
    canvas.height = dh;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, dw, dh);
    ctx.drawImage(img, 0, 0, dw, dh);

    const x = cropRect.sx * displayScale;
    const y = cropRect.sy * displayScale;
    const w = cropRect.sw * displayScale;
    const h = cropRect.sh * displayScale;

    ctx.fillStyle = "rgba(0, 0, 0, 0.45)";
    ctx.fillRect(0, 0, dw, dh);
    ctx.clearRect(x, y, w, h);
    ctx.drawImage(
      img,
      cropRect.sx,
      cropRect.sy,
      cropRect.sw,
      cropRect.sh,
      x,
      y,
      w,
      h,
    );

    ctx.strokeStyle = "rgba(110, 200, 255, 0.95)";
    ctx.lineWidth = 2;
    ctx.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
  };

  const pointerToImage = (clientX: number, clientY: number): { x: number; y: number } | null => {
    if (!canvas || !img.naturalWidth) return null;
    const rect = canvas.getBoundingClientRect();
    const x = (clientX - rect.left) / displayScale;
    const y = (clientY - rect.top) / displayScale;
    if (x < 0 || y < 0 || x > img.naturalWidth || y > img.naturalHeight) return null;
    return { x, y };
  };

  const hitCrop = (x: number, y: number): boolean => {
    return (
      x >= cropRect.sx &&
      x <= cropRect.sx + cropRect.sw &&
      y >= cropRect.sy &&
      y <= cropRect.sy + cropRect.sh
    );
  };

  const onPointerDown = (event: PointerEvent) => {
    if (!canvas) return;
    const pt = pointerToImage(event.clientX, event.clientY);
    if (!pt || !hitCrop(pt.x, pt.y)) return;
    dragging = true;
    dragStart = { x: pt.x, y: pt.y, sx: cropRect.sx, sy: cropRect.sy };
    canvas.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: PointerEvent) => {
    if (!dragging || !dragStart || !img.naturalWidth) return;
    const pt = pointerToImage(event.clientX, event.clientY);
    if (!pt) return;
    const dx = pt.x - dragStart.x;
    const dy = pt.y - dragStart.y;
    cropRect = clampRankBadgeCropRect(
      {
        sx: dragStart.sx + dx,
        sy: dragStart.sy + dy,
        sw: cropRect.sw,
        sh: cropRect.sh,
      },
      img.naturalWidth,
      img.naturalHeight,
    );
    paint();
  };

  const endDrag = (event: PointerEvent) => {
    if (!dragging) return;
    dragging = false;
    dragStart = null;
    canvas?.releasePointerCapture(event.pointerId);
  };

  const applyCrop = () => {
    if (!img.naturalWidth) return;
    const bg = options.exportBg?.() ?? {};
    const dataUrl = exportRankBadgeSheetCrop(img, cropRect, bg);
    options.onApplied(dataUrl);
  };

  const onCancel = () => {
    options.onCancel?.();
  };

  canvas?.addEventListener("pointerdown", onPointerDown);
  canvas?.addEventListener("pointermove", onPointerMove);
  canvas?.addEventListener("pointerup", endDrag);
  canvas?.addEventListener("pointercancel", endDrag);
  host.querySelector("#gm-rank-badge-crop-apply")?.addEventListener("click", applyCrop);
  host.querySelector("#gm-rank-badge-crop-cancel")?.addEventListener("click", onCancel);

  const onResize = () => paint();
  window.addEventListener("resize", onResize);

  img.onload = () => {
    cropRect = maxRankBadgeCropRect(img.naturalWidth, img.naturalHeight);
    paint();
  };
  img.onerror = () => {
    const hint = host.querySelector<HTMLElement>(".gm-rank-badge-crop-hint");
    if (hint) hint.textContent = "โหลดภาพไม่สำเร็จ";
  };
  img.src = sourceDataUrl;

  return {
    destroy: () => {
      window.removeEventListener("resize", onResize);
      canvas?.removeEventListener("pointerdown", onPointerDown);
      canvas?.removeEventListener("pointermove", onPointerMove);
      canvas?.removeEventListener("pointerup", endDrag);
      canvas?.removeEventListener("pointercancel", endDrag);
      host.innerHTML = "";
    },
  };
}
